import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { getApiBaseUrl } from '@core/services/api-base-url';
import { CounsellingIntakeAssessmentResponse, SaveCounsellingIntakeAssessmentRequest } from './assessment/cia/cia.model';
import { normalizePageRequest, PageRequest, PagedResponse } from '@core/models/common.model';
import {
  AssignInterventionAffiliateRequest,
  CompleteInterventionTaskRequest,
  CreateInterventionRequest,
  CreateProviderReferralRequest,
  GrantInterventionConsentRequest,
  SubmitInterventionReportRequest,
  DeclineReferralRequest,
  InterventionAffiliateAssignmentResponse,
  InterventionConsentResponse,
  InterventionReportResponse,
  InterventionListItemResponse,
  InterventionResponse,
  InterventionSessionAttendanceRequest,
  InterventionSessionRequest,
  InterventionSessionResponse,
  InterventionTaskResponse,
  InterventionTransactionHistoryResponse,
  InterventionTypeResponse,
  EligibilityDependantRequest,
  InterventionProgressResponse,
  PresentingProblemCodingRequest,
  PresentingProblemCodingResponse,
  EligibilityMemberRequest,
  EligibilityResponse,
  ReferralPreferenceRequest,
  ReferrerRequest,
  ReferrerResponse,
  ReferralPreferenceResponse,
  ReferralSummaryRequest,
  ReferralSummaryResponse,
  RiskScreenerRequest,
  RiskScreenerResponse,
  SessionNoteRequest,
  SessionNoteResponse,
  ProviderAssignmentResponse,
  ProviderReferralResponse,
  ReassignReferralRequest,
  UpdateInterventionRequest,
  WithdrawReferralRequest,
} from './intervention.model';

@Injectable({ providedIn: 'root' })
export class InterventionApiService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = getApiBaseUrl();

  getInterventions(
    pageRequest?: PageRequest,
    search?: string | null,
    includeInactive = true,
  ): Observable<PagedResponse<InterventionListItemResponse>> {
    const page = normalizePageRequest(pageRequest);
    return this.http.get<PagedResponse<InterventionListItemResponse>>(
      `${this.apiBaseUrl}interventions`,
      {
        params: {
          search: search ?? '',
          pageNumber: page.pageNumber,
          pageSize: page.pageSize,
          includeInactive,
        },
      },
    );
  }

  getIntervention(id: string, includeInactive = true): Observable<InterventionResponse> {
    return this.http.get<InterventionResponse>(`${this.apiBaseUrl}interventions/${id}`, {
      params: { includeInactive },
    });
  }

  createIntervention(request: CreateInterventionRequest): Observable<InterventionResponse> {
    return this.http.post<InterventionResponse>(`${this.apiBaseUrl}interventions`, request);
  }

  updateIntervention(id: string, request: UpdateInterventionRequest): Observable<InterventionResponse> {
    return this.http.put<InterventionResponse>(`${this.apiBaseUrl}interventions/${id}`, request);
  }

  getInterventionTypes(includeInactive = false): Observable<InterventionTypeResponse[]> {
    return this.http.get<InterventionTypeResponse[]>(`${this.apiBaseUrl}intervention-types`, {
      params: { includeInactive },
    });
  }

  getLifecycleStages(interventionTypeId: string): Observable<string[]> {
    return this.http.get<string[]>(
      `${this.apiBaseUrl}intervention-types/${interventionTypeId}/lifecycle-stages`,
    );
  }

  getInterventionHistory(interventionId: string): Observable<InterventionTransactionHistoryResponse[]> {
    return this.http.get<InterventionTransactionHistoryResponse[]>(
      `${this.apiBaseUrl}interventions/${interventionId}/history`,
    );
  }

  assignAffiliate(
    interventionId: string,
    request: AssignInterventionAffiliateRequest,
  ): Observable<InterventionAffiliateAssignmentResponse> {
    return this.http.post<InterventionAffiliateAssignmentResponse>(
      `${this.apiBaseUrl}interventions/${interventionId}/affiliate-assignments`,
      request,
    );
  }

  getReferrals(interventionId: string): Observable<ProviderReferralResponse[]> {
    return this.http.get<ProviderReferralResponse[]>(
      `${this.apiBaseUrl}interventions/${interventionId}/referrals`,
    );
  }

  createReferral(
    interventionId: string,
    request: CreateProviderReferralRequest,
  ): Observable<ProviderReferralResponse> {
    return this.http.post<ProviderReferralResponse>(
      `${this.apiBaseUrl}interventions/${interventionId}/referrals`,
      request,
    );
  }

  acceptReferral(referralId: string, affiliateId: string): Observable<ProviderAssignmentResponse> {
    return this.http.post<ProviderAssignmentResponse>(
      `${this.apiBaseUrl}referrals/${referralId}/affiliates/${affiliateId}/accept`,
      {},
    );
  }

  declineReferral(
    referralId: string,
    affiliateId: string,
    request: DeclineReferralRequest,
  ): Observable<ProviderAssignmentResponse> {
    return this.http.post<ProviderAssignmentResponse>(
      `${this.apiBaseUrl}referrals/${referralId}/affiliates/${affiliateId}/decline`,
      request,
    );
  }

  withdrawReferral(
    referralId: string,
    affiliateId: string,
    request: WithdrawReferralRequest,
  ): Observable<ProviderAssignmentResponse> {
    return this.http.post<ProviderAssignmentResponse>(
      `${this.apiBaseUrl}referrals/${referralId}/affiliates/${affiliateId}/withdraw`,
      request,
    );
  }

  reassignReferral(
    referralId: string,
    request: ReassignReferralRequest,
  ): Observable<ProviderReferralResponse> {
    return this.http.post<ProviderReferralResponse>(
      `${this.apiBaseUrl}referrals/${referralId}/reassign`,
      request,
    );
  }

  getReports(interventionId: string): Observable<InterventionReportResponse[]> {
    return this.http.get<InterventionReportResponse[]>(
      `${this.apiBaseUrl}interventions/${interventionId}/reports`,
    );
  }

  submitReport(
    interventionId: string,
    request: SubmitInterventionReportRequest,
  ): Observable<InterventionReportResponse> {
    return this.http.post<InterventionReportResponse>(
      `${this.apiBaseUrl}interventions/${interventionId}/reports`,
      request,
    );
  }

  reviewReport(
    interventionId: string,
    reportId: string,
    action: 'approve' | 'return',
    notes: string | null,
  ): Observable<InterventionReportResponse> {
    return this.http.post<InterventionReportResponse>(
      `${this.apiBaseUrl}interventions/${interventionId}/reports/${reportId}/${action}`,
      { notes },
    );
  }

  getReferralSummary(interventionId: string): Observable<ReferralSummaryResponse> {
    return this.http.get<ReferralSummaryResponse>(`${this.apiBaseUrl}interventions/${interventionId}/referral-summary`);
  }

  saveReferralSummary(interventionId: string, request: ReferralSummaryRequest): Observable<ReferralSummaryResponse> {
    return this.http.put<ReferralSummaryResponse>(
      `${this.apiBaseUrl}interventions/${interventionId}/referral-summary`,
      request,
    );
  }

  getReferralPreference(interventionId: string): Observable<ReferralPreferenceResponse> {
    return this.http.get<ReferralPreferenceResponse>(`${this.apiBaseUrl}interventions/${interventionId}/referral-preference`);
  }

  saveReferralPreference(interventionId: string, request: ReferralPreferenceRequest): Observable<ReferralPreferenceResponse> {
    return this.http.put<ReferralPreferenceResponse>(
      `${this.apiBaseUrl}interventions/${interventionId}/referral-preference`,
      request,
    );
  }

  getProgress(interventionId: string): Observable<InterventionProgressResponse> {
    return this.http.get<InterventionProgressResponse>(`${this.apiBaseUrl}interventions/${interventionId}/progress`);
  }

  getPresentingProblem(interventionId: string): Observable<PresentingProblemCodingResponse> {
    return this.http.get<PresentingProblemCodingResponse>(`${this.apiBaseUrl}interventions/${interventionId}/presenting-problem`);
  }

  savePresentingProblem(interventionId: string, request: PresentingProblemCodingRequest): Observable<PresentingProblemCodingResponse> {
    return this.http.put<PresentingProblemCodingResponse>(`${this.apiBaseUrl}interventions/${interventionId}/presenting-problem`, request);
  }

  getReferrer(interventionId: string): Observable<ReferrerResponse> {
    return this.http.get<ReferrerResponse>(`${this.apiBaseUrl}interventions/${interventionId}/referrer`);
  }

  saveReferrer(interventionId: string, request: ReferrerRequest): Observable<ReferrerResponse> {
    return this.http.put<ReferrerResponse>(`${this.apiBaseUrl}interventions/${interventionId}/referrer`, request);
  }

  removeReferrer(interventionId: string): Observable<ReferrerResponse> {
    return this.http.delete<ReferrerResponse>(`${this.apiBaseUrl}interventions/${interventionId}/referrer`);
  }

  getEligibility(interventionId: string): Observable<EligibilityResponse> {
    return this.http.get<EligibilityResponse>(`${this.apiBaseUrl}interventions/${interventionId}/eligibility`);
  }

  saveEligibilityMember(interventionId: string, request: EligibilityMemberRequest): Observable<EligibilityResponse> {
    return this.http.put<EligibilityResponse>(`${this.apiBaseUrl}interventions/${interventionId}/eligibility/member`, request);
  }

  saveEligibilityProducts(interventionId: string, momentumProductIds: string[]): Observable<EligibilityResponse> {
    return this.http.put<EligibilityResponse>(`${this.apiBaseUrl}interventions/${interventionId}/eligibility/products`, { momentumProductIds });
  }

  captureEligibilityDependant(interventionId: string, request: EligibilityDependantRequest): Observable<EligibilityResponse> {
    return this.http.put<EligibilityResponse>(`${this.apiBaseUrl}interventions/${interventionId}/eligibility/dependant`, request);
  }

  decideEligibility(interventionId: string, eligible: boolean, detail: string | null): Observable<EligibilityResponse> {
    return this.http.post<EligibilityResponse>(`${this.apiBaseUrl}interventions/${interventionId}/eligibility/decision`, { eligible, detail });
  }

  getSessionNotes(interventionId: string): Observable<SessionNoteResponse[]> {
    return this.http.get<SessionNoteResponse[]>(`${this.apiBaseUrl}interventions/${interventionId}/session-notes`);
  }

  saveSessionNote(interventionId: string, sessionId: string, request: SessionNoteRequest): Observable<SessionNoteResponse> {
    return this.http.put<SessionNoteResponse>(
      `${this.apiBaseUrl}interventions/${interventionId}/sessions/${sessionId}/notes`,
      request,
    );
  }

  // The clinical assessment (CIA) of the case: one for each case, saved with a single PUT whose first call creates it.
  getCia(interventionId: string): Observable<CounsellingIntakeAssessmentResponse> {
    return this.http.get<CounsellingIntakeAssessmentResponse>(`${this.apiBaseUrl}interventions/${interventionId}/counselling-intake-assessment`);
  }

  saveCia(interventionId: string, request: SaveCounsellingIntakeAssessmentRequest): Observable<CounsellingIntakeAssessmentResponse> {
    return this.http.put<CounsellingIntakeAssessmentResponse>(`${this.apiBaseUrl}interventions/${interventionId}/counselling-intake-assessment`, request);
  }

  finalizeCia(interventionId: string): Observable<CounsellingIntakeAssessmentResponse> {
    return this.http.put<CounsellingIntakeAssessmentResponse>(`${this.apiBaseUrl}interventions/${interventionId}/counselling-intake-assessment/finalize`, {});
  }

  reopenCia(interventionId: string): Observable<CounsellingIntakeAssessmentResponse> {
    return this.http.put<CounsellingIntakeAssessmentResponse>(`${this.apiBaseUrl}interventions/${interventionId}/counselling-intake-assessment/reopen`, {});
  }

  getRiskScreeners(interventionId: string): Observable<RiskScreenerResponse[]> {
    return this.http.get<RiskScreenerResponse[]>(`${this.apiBaseUrl}interventions/${interventionId}/risk-screeners`);
  }

  createRiskScreener(interventionId: string, request: RiskScreenerRequest): Observable<RiskScreenerResponse> {
    return this.http.post<RiskScreenerResponse>(
      `${this.apiBaseUrl}interventions/${interventionId}/risk-screeners`,
      request,
    );
  }

  getConsents(interventionId: string): Observable<InterventionConsentResponse[]> {
    return this.http.get<InterventionConsentResponse[]>(
      `${this.apiBaseUrl}interventions/${interventionId}/consents`,
    );
  }

  grantConsent(
    interventionId: string,
    request: GrantInterventionConsentRequest,
  ): Observable<InterventionConsentResponse> {
    return this.http.post<InterventionConsentResponse>(
      `${this.apiBaseUrl}interventions/${interventionId}/consents`,
      request,
    );
  }

  withdrawConsent(
    interventionId: string,
    consentId: string,
    reason: string,
  ): Observable<InterventionConsentResponse> {
    return this.http.post<InterventionConsentResponse>(
      `${this.apiBaseUrl}interventions/${interventionId}/consents/${consentId}/withdraw`,
      { reason },
    );
  }

  getSessions(interventionId: string): Observable<InterventionSessionResponse[]> {
    return this.http.get<InterventionSessionResponse[]>(
      `${this.apiBaseUrl}interventions/${interventionId}/sessions`,
    );
  }

  scheduleSession(
    interventionId: string,
    request: InterventionSessionRequest,
  ): Observable<InterventionSessionResponse> {
    return this.http.post<InterventionSessionResponse>(
      `${this.apiBaseUrl}interventions/${interventionId}/sessions`,
      request,
    );
  }

  updateSession(
    interventionId: string,
    sessionId: string,
    request: InterventionSessionRequest,
  ): Observable<InterventionSessionResponse> {
    return this.http.put<InterventionSessionResponse>(
      `${this.apiBaseUrl}interventions/${interventionId}/sessions/${sessionId}`,
      request,
    );
  }

  cancelSession(interventionId: string, sessionId: string): Observable<InterventionSessionResponse> {
    return this.http.post<InterventionSessionResponse>(
      `${this.apiBaseUrl}interventions/${interventionId}/sessions/${sessionId}/cancel`,
      {},
    );
  }

  captureAttendance(
    interventionId: string,
    sessionId: string,
    request: InterventionSessionAttendanceRequest,
  ): Observable<InterventionSessionResponse> {
    return this.http.post<InterventionSessionResponse>(
      `${this.apiBaseUrl}interventions/${interventionId}/sessions/${sessionId}/attendance`,
      request,
    );
  }

  verifyAttendance(interventionId: string, sessionId: string): Observable<InterventionSessionResponse> {
    return this.http.post<InterventionSessionResponse>(
      `${this.apiBaseUrl}interventions/${interventionId}/sessions/${sessionId}/attendance/verify`,
      {},
    );
  }

  getTasks(interventionId: string): Observable<InterventionTaskResponse[]> {
    return this.http.get<InterventionTaskResponse[]>(
      `${this.apiBaseUrl}interventions/${interventionId}/tasks`,
    );
  }

  completeTask(
    interventionId: string,
    taskId: string,
    request: CompleteInterventionTaskRequest,
  ): Observable<InterventionTaskResponse> {
    return this.http.post<InterventionTaskResponse>(
      `${this.apiBaseUrl}interventions/${interventionId}/tasks/${taskId}/complete`,
      request,
    );
  }
}
